import { AbstractConnector } from '@web3-react/abstract-connector'
import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core'
import { darken, lighten } from 'polished'
import React, { useMemo } from 'react'
import { Activity } from 'react-feather'
// import { isMobile } from 'react-device-detect'
// import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import CoinbaseWalletIcon from '../../assets/images/coinbaseWalletIcon.svg'
import FortmaticIcon from '../../assets/images/fortmaticIcon.png'
import PortisIcon from '../../assets/images/portisIcon.png'
import WalletConnectIcon from '../../assets/images/walletConnectIcon.svg'
import { fortmatic, injected, portis, walletconnect, walletlink } from '../../connectors'
import { NetworkContextName } from '../../constants'
import useENSName from '../../hooks/useENSName'
import { useHasSocks } from '../../hooks/useSocksBalance'
import { useWalletModalToggle } from '../../state/application/hooks'
import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/reducer'
import { shortenAddress } from '../../utils'
import { ButtonSecondary } from '../Button'

import Identicon from '../Identicon'
import Loader from '../Loader'

import { RowBetween } from '../Row'
import WalletModal from '../WalletModal'
import { switchOrAddNetwork } from '../../utils/switchOrAddNetwork'

// 检测移动端钱包注入提供者并统一到 window.ethereum
function ensureMobileWalletProvider(): boolean {
  if (typeof window === 'undefined') return false

  // 如果已经有标准的 window.ethereum，直接返回 true
  if (window.ethereum) {
    return true
  }

  // 检查 Bitget Wallet (可能使用 bitkeep.ethereum 或 bitget.ethereum)
  let provider = null
  if ((window as any).bitkeep?.ethereum) {
    provider = (window as any).bitkeep.ethereum
  } else if ((window as any).bitget?.ethereum) {
    provider = (window as any).bitget.ethereum
  } else if ((window as any).web3?.currentProvider) {
    provider = (window as any).web3.currentProvider
  }

  // 如果找到了非标准的提供者，将其赋值给 window.ethereum
  if (provider) {
    ;(window as any).ethereum = provider
    return true
  }

  return false
}

const IconWrapper = styled.div<{ size?: number }>`
  ${({ theme }) => theme.flexColumnNoWrap};
  align-items: center;
  justify-content: center;
  & > * {
    height: ${({ size }) => (size ? size + 'px' : '32px')};
    width: ${({ size }) => (size ? size + 'px' : '32px')};
  }
`

const Web3StatusGeneric = styled(ButtonSecondary)`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  align-items: center;
  padding: 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  :focus {
    outline: none;
  }
`
const Web3StatusError = styled(Web3StatusGeneric)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
`

const Web3StatusConnect = styled(Web3StatusGeneric)<{ faded?: boolean }>`
  background-color: #e7ac08;
  border: none;
  color: #fff;
  font-weight: 500;
  border-radius: 20px;

  :hover,
  :active {
    opacity: 0.8;
    border: none;
    box-shadow: none;
  }

  :focus {
    border: none;
    outline: none;
    box-shadow: none;
  }

  ${({ faded }) =>
    faded &&
    css`
      background-color: #e7ac08;
      border: none;
      color: #fff;

      :hover,
      :active {
        opacity: 0.8;
        border: none;
        box-shadow: none;
      }

      :focus {
        border: none;
        outline: none;
        box-shadow: none;
      }
    `}
`

const Web3StatusConnected = styled(Web3StatusGeneric)<{ pending?: boolean }>`
  background-color: ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg2)};
  border: 1px solid ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg3)};
  color: ${({ pending, theme }) => (pending ? theme.white : theme.text1)};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ pending, theme }) => (pending ? darken(0.05, theme.primary1) : lighten(0.05, theme.bg2))};

    :focus {
      border: 1px solid ${({ pending, theme }) => (pending ? darken(0.1, theme.primary1) : darken(0.1, theme.bg3))};
    }
  }
`

const Text = styled.p`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0.5rem 0 0.25rem;
  font-size: 1rem;
  width: fit-content;
  font-weight: 500;
`

const NetworkIcon = styled(Activity)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

const SOCK = (
  <span role="img" aria-label="has socks emoji" style={{ marginTop: -4, marginBottom: -4 }}>
    🧦
  </span>
)

// eslint-disable-next-line react/prop-types
function StatusIcon({ connector }: { connector: AbstractConnector }) {
  if (connector === injected) {
    return <Identicon />
  } else if (connector === walletconnect) {
    return (
      <IconWrapper size={16}>
        <img src={WalletConnectIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === walletlink) {
    return (
      <IconWrapper size={16}>
        <img src={CoinbaseWalletIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === fortmatic) {
    return (
      <IconWrapper size={16}>
        <img src={FortmaticIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === portis) {
    return (
      <IconWrapper size={16}>
        <img src={PortisIcon} alt={''} />
      </IconWrapper>
    )
  }
  return null
}

function Web3StatusInner() {
  // const { t } = useTranslation()
  const { account, connector, error, activate } = useWeb3React()

  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter(tx => !tx.receipt).map(tx => tx.hash)

  const hasPendingTransactions = !!pending.length
  const hasSocks = useHasSocks()
  const toggleWalletModal = useWalletModalToggle()

  // 检查并切换到 Mova 主网络的辅助函数
  const ensureMovaNetwork = async (provider: any): Promise<void> => {
    if (!provider || typeof provider.request !== 'function') return

    try {
      // 获取当前链 ID
      const currentChainId = await provider.request({ method: 'eth_chainId' })
      const TARGET_CHAIN_ID = parseInt(process.env.REACT_APP_CHAIN_ID ?? '61900')
      const currentChainIdNum =
        TARGET_CHAIN_ID === 61900
          ? 61900
          : typeof currentChainId === 'string'
          ? parseInt(currentChainId, 16)
          : Number(currentChainId)

      // 如果当前不是 Mova 主网，则切换
      if (currentChainIdNum !== TARGET_CHAIN_ID) {
        console.log(`Current chain ID: ${currentChainIdNum}, switching to Mova Mainnet (${TARGET_CHAIN_ID})`)
        await switchOrAddNetwork(provider)
      }
    } catch (error) {
      console.warn('Failed to check/switch network:', error)
      // 即使检查失败，也尝试切换网络（可能是钱包不支持 eth_chainId）
      await switchOrAddNetwork(provider)
    }
  }

  // 移动端直接连接钱包的处理函数
  const handleConnectClick = async () => {
    // 确保钱包提供者已正确设置
    const hasProvider = ensureMobileWalletProvider()
    if (hasProvider && window.ethereum) {
      try {
        const ethereum = window.ethereum as any
        if (ethereum.request) {
          // 先检查并切换到 Mova 主网络
          await ensureMovaNetwork(ethereum)
        }
        // 直接连接，不使用 silent mode，以便触发授权弹窗
        await activate(injected, undefined, undefined)
        return // 连接成功，不需要打开弹窗
      } catch (err) {
        // 连接失败，打开弹窗让用户选择
        console.error('Direct connection failed:', err)
        toggleWalletModal()
      }
    } else {
      // 没有检测到钱包，打开弹窗
      toggleWalletModal()
    }
  }

  if (account) {
    return (
      <Web3StatusConnected id="web3-status-connected" onClick={toggleWalletModal} pending={hasPendingTransactions}>
        {hasPendingTransactions ? (
          <RowBetween>
            <Text>{pending?.length} Pending</Text> <Loader stroke="white" />
          </RowBetween>
        ) : (
          <>
            {hasSocks ? SOCK : null}
            <Text>{ENSName || shortenAddress(account)}</Text>
          </>
        )}
        {!hasPendingTransactions && connector && <StatusIcon connector={connector} />}
      </Web3StatusConnected>
    )
  } else if (error) {
    return (
      <Web3StatusError
        onClick={() => {
          if (error instanceof UnsupportedChainIdError) {
            switchOrAddNetwork()
          } else {
            toggleWalletModal()
          }
        }}
      >
        <NetworkIcon />
        <Text>{error instanceof UnsupportedChainIdError ? 'Wrong Network' : 'Error'}</Text>
      </Web3StatusError>
    )
  } else {
    return (
      <Web3StatusConnect id="connect-wallet" onClick={handleConnectClick} faded={!account}>
        <Text>Connect Wallet</Text>
      </Web3StatusConnect>
    )
  }
}

export default function Web3Status() {
  const { active, account } = useWeb3React()
  const contextNetwork = useWeb3React(NetworkContextName)

  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter(tx => !tx.receipt).map(tx => tx.hash)
  const confirmed = sortedRecentTransactions.filter(tx => tx.receipt).map(tx => tx.hash)

  if (!contextNetwork.active && !active) {
    return null
  }

  return (
    <>
      <Web3StatusInner />
      <WalletModal ENSName={ENSName ?? undefined} pendingTransactions={pending} confirmedTransactions={confirmed} />
    </>
  )
}
